import { useState } from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatNumber } from '@/lib/currency.utils';
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";

export interface IndexFundHoldingsTableProps {
  holdings: IndexFundHolding[];
  prices: Record<string, IndexFundPrice | null>;
  pricesLoading?: Record<string, boolean>;
  onEdit: (holding: IndexFundHolding) => void;
  onDelete: (holding: IndexFundHolding) => void;
  onRowClick: (holding: IndexFundHolding) => void;
}

type SortKey = 'totalValue' | 'totalReturn' | null;
type SortDir = 'asc' | 'desc';

const CATEGORY_BADGE_CLASSES: Record<IndexFundCategory, string> = {
  large_cap_equity: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  mid_cap_equity: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  small_cap_equity: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  emerging_markets: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  developed_markets: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  global_equity: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  fixed_income: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  real_estate: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  commodity: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  multi_asset: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
};

const CATEGORY_LABELS: Record<IndexFundCategory, string> = {
  large_cap_equity: 'Large Cap',
  mid_cap_equity: 'Mid Cap',
  small_cap_equity: 'Small Cap',
  emerging_markets: 'Emerging Markets',
  developed_markets: 'Developed Markets',
  global_equity: 'Global Equity',
  fixed_income: 'Fixed Income',
  real_estate: 'Real Estate',
  commodity: 'Commodity',
  multi_asset: 'Multi Asset',
};

function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function computeMetrics(holding: IndexFundHolding, nav: number) {
  const totalValue = holding.units_held * nav;
  const totalReturn = (nav - holding.average_cost_per_unit) * holding.units_held;
  const totalReturnPct =
    holding.average_cost_per_unit > 0
      ? ((nav - holding.average_cost_per_unit) / holding.average_cost_per_unit) * 100
      : 0;
  return { totalValue, totalReturn, totalReturnPct };
}

export function IndexFundHoldingsTable({
  holdings,
  prices,
  pricesLoading = {},
  onEdit,
  onDelete,
  onRowClick,
}: IndexFundHoldingsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sorted = [...holdings].sort((a, b) => {
    if (!sortKey) return 0;
    const priceA = prices[a.fund_symbol]?.price ?? null;
    const priceB = prices[b.fund_symbol]?.price ?? null;
    if (priceA === null && priceB === null) return 0;
    if (priceA === null) return 1;
    if (priceB === null) return -1;

    const metricsA = computeMetrics(a, priceA);
    const metricsB = computeMetrics(b, priceB);

    const valA = sortKey === 'totalValue' ? metricsA.totalValue : metricsA.totalReturn;
    const valB = sortKey === 'totalValue' ? metricsB.totalValue : metricsB.totalReturn;

    return sortDir === 'desc' ? valB - valA : valA - valB;
  });

  if (holdings.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground text-sm"
        data-testid="empty-state"
      >
        No index fund holdings yet. Add your first index fund to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full">
      <Table aria-label="Index fund holdings">
        <TableHeader>
          <TableRow>
            <TableHead>Fund</TableHead>
            <TableHead>Benchmark Index</TableHead>
            <TableHead className="text-right">Units</TableHead>
            <TableHead className="text-right">Avg Cost</TableHead>
            <TableHead className="text-right">Current NAV</TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 font-medium hover:bg-transparent"
                onClick={() => toggleSort('totalValue')}
                data-testid="sort-total-value"
              >
                Total Value
                <ArrowUpDown className="ml-1 h-3 w-3" />
              </Button>
            </TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 font-medium hover:bg-transparent"
                onClick={() => toggleSort('totalReturn')}
                data-testid="sort-total-return"
              >
                Total Return
                <ArrowUpDown className="ml-1 h-3 w-3" />
              </Button>
            </TableHead>
            <TableHead className="text-right">Expense Ratio</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((holding) => {
            const priceData = prices[holding.fund_symbol];
            const isLoadingPrice = pricesLoading[holding.fund_symbol] ?? false;
            const metrics = priceData ? computeMetrics(holding, priceData.price) : null;
            const isPositive = metrics ? metrics.totalReturn > 0 : null;
            const isNegative = metrics ? metrics.totalReturn < 0 : null;

            return (
              <TableRow
                key={holding.id}
                className="cursor-pointer"
                onClick={() => onRowClick(holding)}
                data-testid={`holding-row-${holding.id}`}
              >
                {/* Fund column */}
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-sm">{holding.fund_symbol}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                      {holding.fund_name}
                    </span>
                    <Badge
                      className={`text-xs w-fit px-1.5 py-0 border-0 ${CATEGORY_BADGE_CLASSES[holding.fund_category]}`}
                      data-testid={`category-badge-${holding.id}`}
                    >
                      {CATEGORY_LABELS[holding.fund_category]}
                    </Badge>
                  </div>
                </TableCell>

                {/* Benchmark Index */}
                <TableCell className="text-sm text-muted-foreground max-w-[140px] truncate">
                  {holding.benchmark_index}
                </TableCell>

                {/* Units */}
                <TableCell className="text-right font-mono text-sm">
                  {formatNumber(holding.units_held, 4)}
                </TableCell>

                {/* Avg Cost */}
                <TableCell className="text-right font-mono text-sm">
                  {formatCurrency(holding.average_cost_per_unit, holding.currency)}
                </TableCell>

                {/* Current NAV */}
                <TableCell className="text-right font-mono text-sm">
                  {isLoadingPrice ? (
                    <span
                      className="flex items-center justify-end gap-1 text-muted-foreground"
                      data-testid={`nav-loading-${holding.id}`}
                    >
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span className="text-xs">Loading…</span>
                    </span>
                  ) : priceData ? (
                    <div className="flex flex-col items-end">
                      <span>{formatCurrency(priceData.price, priceData.currency)}</span>
                      <span
                        className={`text-xs flex items-center gap-0.5 ${
                          priceData.change > 0
                            ? 'text-emerald-600'
                            : priceData.change < 0
                            ? 'text-red-600'
                            : 'text-muted-foreground'
                        }`}
                        data-testid={`nav-change-${holding.id}`}
                      >
                        {priceData.change > 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : priceData.change < 0 ? (
                          <TrendingDown className="h-3 w-3" />
                        ) : null}
                        {priceData.changePercent.toFixed(2)}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Total Value */}
                <TableCell className="text-right font-mono text-sm">
                  {metrics ? (
                    formatCurrency(metrics.totalValue, holding.currency)
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>

                {/* Total Return */}
                <TableCell className="text-right">
                  {metrics === null ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div
                      className={`flex flex-col items-end text-sm font-mono ${
                        isPositive
                          ? 'text-emerald-600'
                          : isNegative
                          ? 'text-red-600'
                          : 'text-muted-foreground'
                      }`}
                      data-testid={`total-return-${holding.id}`}
                    >
                      <span>
                        {isPositive ? '+' : ''}
                        {formatCurrency(metrics.totalReturn, holding.currency)}
                      </span>
                      <span className="text-xs">
                        {isPositive ? '+' : ''}
                        {metrics.totalReturnPct.toFixed(2)}%
                      </span>
                    </div>
                  )}
                </TableCell>

                {/* Expense Ratio */}
                <TableCell className="text-right font-mono text-sm">
                  {holding.expense_ratio != null
                    ? `${(holding.expense_ratio * 100).toFixed(2)}%`
                    : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(holding);
                      }}
                      aria-label={`Edit ${holding.fund_symbol}`}
                      data-testid={`edit-btn-${holding.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(holding);
                      }}
                      aria-label={`Delete ${holding.fund_symbol}`}
                      data-testid={`delete-btn-${holding.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
